import { useTranslation } from 'react-i18next';

// Layout components
import { Header } from './Header';
import { Footer } from './Footer';
import { Hero2 } from './homepage/Hero2';

// Section components
import { IntegrationFeatures } from './homepage/IntegrationFeatures';
import { ApiFeatures } from './homepage/ApiFeatures';
import { CtaSection } from './homepage/CtaSection';
import { ContactForm } from './homepage/ContactForm';

const integrations = [
  { src: '/images/integrations/opera.png',        alt: 'Opera PMS' },
  { src: '/images/integrations/mews.png',         alt: 'Mews' },
  { src: '/images/integrations/stayntouch.png',   alt: 'StayNTouch' },
  { src: '/images/integrations/apaleo.svg',       alt: 'Apaleo' },
  { src: '/images/integrations/thais-pms.svg',    alt: 'Thais PMS' },
  { src: '/images/integrations/medialog.png',     alt: 'MediaLog PMS' },
  { src: '/images/integrations/lean-pms.png',     alt: 'Lean PMS' },
  { src: '/images/integrations/roomkey-pms.png',  alt: 'RoomKey PMS' },
  { src: '/images/integrations/misterbooking.png', alt: 'Misterbooking' },
];

export function HomePage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gray-900 dark">
      <Header />

      {/* Hero */}
      <div className="pt-16">
        <Hero2 />
      </div>

      {/* Integration Features */}
      <IntegrationFeatures />

      {/* Our Integrations */}
      <div className="bg-[#0d1523] py-24 overflow-hidden">
        <div className="text-center mb-14 px-4">
          <h2 className="text-4xl text-white mb-4">{t('ourIntegrations.title')}</h2>
          <p className="text-xl text-gray-400">{t('ourIntegrations.subtitle')}</p>
        </div>

        {/* Infinite carousel */}
        <div className="relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to right, #0d1523, transparent)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(to left, #0d1523, transparent)' }} />

          <div className="flex animate-marquee gap-6 w-max">
            {[...integrations, ...integrations].map(({ src, alt }, i) => (
              <div
                key={i}
                className="flex-shrink-0 bg-white rounded-xl p-5 flex items-center justify-center w-44 h-24 shadow-md"
              >
                <img src={src} alt={alt} className="max-h-10 max-w-full object-contain" />
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <p className="text-gray-500 italic text-sm">{t('ourIntegrations.moreToCome')}</p>
        </div>
      </div>

      {/* API Features */}
      <ApiFeatures />

      {/* Pricing */}
      <div className="relative bg-[#0d1523] py-28 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[#8B5CF6] opacity-[0.05] blur-[100px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-14">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">{t('pricing.title')}</h2>
            <div className="flex items-baseline justify-center gap-2 mt-4">
              <span className="text-gray-400 text-lg">{t('pricing.from')}</span>
              <span className="text-5xl font-extrabold bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(90deg, #8B5CF6, #D946A8)' }}>
                {t('pricing.fromPrice')}
              </span>
              <span className="text-gray-400 text-lg">{t('pricing.fromUnit')}</span>
            </div>
            <p className="text-gray-500 text-sm mt-3">{t('pricing.noCommitment')}</p>
          </div>

          {/* Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(t('pricing.tiers', { returnObjects: true }) as { range: string; price: string; unit: string; highlight: boolean }[]).map((tier, i) => (
              <div
                key={i}
                className={`relative rounded-2xl p-8 text-center transition-all duration-300 hover:-translate-y-1 ${
                  tier.highlight
                    ? 'bg-[#111827] border-2 border-[#8B5CF6] shadow-lg shadow-[#8B5CF6]/20'
                    : 'bg-[#111827] border border-white/5 hover:border-white/10'
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold text-white"
                      style={{ background: 'linear-gradient(90deg, #8B5CF6, #D946A8)' }}>
                      Meilleur tarif
                    </span>
                  </div>
                )}

                <p className="text-gray-400 text-sm mb-6">{tier.range}</p>

                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <span className={`text-5xl font-extrabold ${tier.highlight ? 'text-white' : 'text-white'}`}>
                    {tier.price}
                  </span>
                </div>
                <p className="text-gray-500 text-sm">{tier.unit}</p>

                {tier.highlight && (
                  <div className="mt-6 h-px w-full bg-gradient-to-r from-transparent via-[#8B5CF6]/40 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <ContactForm />

      {/* CTA Section */}
      <CtaSection />

      <Footer />
    </div>
  );
}
